"use client"

import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import { BookingCta } from "@/components/ui/booking-cta"
import { CtaBanner } from "@/components/ui/cta-banner"
import { FaqSection, type FaqAccordionItem } from "@/components/ui/faq-section"
import { ArrowRightIcon, buttonVariants } from "@/components/ui/button"
import {
  DropletIcon,
  GreenCheck,
  InfoIcon,
  PersonIcon,
  ScaleIcon,
  SwissShieldIcon,
  TestTubeIcon,
  WarningIcon,
  XCircleIcon,
} from "@/components/ui/icons"
import { fib4CalculatorKeys as k } from "@/i18n/fib4-calculator-keys"
import { blobMediaUrl } from "@/lib/blob-url"
import { useCountry } from "@/components/layout/country-provider"
import { baseBloodTest } from "@/lib/base-blood-test"
import { isAllowedInCountry } from "@/lib/country-gates"
import { AGE_GROUP_ORDER, BAND_ORDER } from "@/lib/fib4"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { cn } from "@/lib/utils"
import { OtherToolsSection } from "../../../_components/other-tools-section"
import { BAND_STYLES, Fib4CalculatorWidget } from "./fib4-calculator-widget"

/**
 * Icons are pre-built elements rather than component references: the shared
 * `icons.tsx` set is split between a `width`/`height` prop shape and a `size`
 * one, so a single `<Icon width={28} …>` call site can't cover both. Building
 * the element here keeps each icon called the way it's declared.
 */
const ICON_CLASS = "text-aubergine-800"

/** The four things the score reads, in the order the form asks for them. */
const INPUTS = [
  {
    titleKey: k.input1Title,
    bodyKey: k.input1Body,
    icon: <PersonIcon width={28} height={28} className={ICON_CLASS} />,
  },
  {
    titleKey: k.input2Title,
    bodyKey: k.input2Body,
    icon: <TestTubeIcon width={28} height={28} className={ICON_CLASS} />,
  },
  {
    titleKey: k.input3Title,
    bodyKey: k.input3Body,
    icon: <ScaleIcon width={28} height={28} className={ICON_CLASS} />,
  },
  {
    titleKey: k.input4Title,
    bodyKey: k.input4Body,
    icon: <DropletIcon width={28} height={28} className={ICON_CLASS} />,
  },
]

const LIMITS = [
  {
    titleKey: k.limit1Title,
    bodyKey: k.limit1Body,
    icon: <ScaleIcon width={28} height={28} className={ICON_CLASS} />,
  },
  {
    titleKey: k.limit2Title,
    bodyKey: k.limit2Body,
    icon: <PersonIcon width={28} height={28} className={ICON_CLASS} />,
  },
  {
    titleKey: k.limit3Title,
    bodyKey: k.limit3Body,
    icon: <DropletIcon width={28} height={28} className={ICON_CLASS} />,
  },
  {
    titleKey: k.limit4Title,
    bodyKey: k.limit4Body,
    icon: <WarningIcon width={28} height={28} className={ICON_CLASS} />,
  },
]

const WHO_FOR = [k.whoFor1, k.whoFor2, k.whoFor3]
const WHO_NOT_FOR = [k.whoNotFor1, k.whoNotFor2, k.whoNotFor3, k.whoNotFor4]

const MEMBERSHIP_FEATURES = [
  k.memFeature1,
  k.memFeature2,
  k.memFeature3,
  k.memFeature4,
  k.memFeature5,
]

const SECTION = "py-16 md:py-24"
const CONTAINER = "mx-auto max-w-[1440px] px-4 md:px-10"
/** Prose sections read at a narrower measure than the 1440 grid. */
const PROSE = "mx-auto max-w-[760px]"

export default function Fib4CalculatorClient({
  initialFaqs = [],
}: {
  initialFaqs?: FaqAccordionItem[]
}) {
  const t = useTranslations("fib4Calculator")
  const lp = useLocalePath()
  const { code: country } = useCountry()

  /**
   * Which promise the closing panel is allowed to make, read from the registry
   * rather than compared against a country literal.
   *
   * `page:membership` is the same gate the panel's own secondary link points
   * at, so this cannot drift from it: a market without that page cannot be
   * shown a link into it. NL is that market, since it sells no membership at
   * all (AHD-9361, "no page, no promise"), so there the panel points at the
   * advanced blood test, the product this market does sell, and at /pricing.
   * The copy follows the link: the `mem*` keys carry check-up wording in
   * messages/nl-NL.json and messages/en-NL.json rather than membership wording.
   */
  const hasMembership = isAllowedInCountry("page:membership", country)

  const faqFallback: FaqAccordionItem[] = [
    { question: t(k.faqQ1), answer: t(k.faqA1) },
    { question: t(k.faqQ2), answer: t(k.faqA2) },
    { question: t(k.faqQ3), answer: t(k.faqA3) },
    { question: t(k.faqQ4), answer: t(k.faqA4) },
    { question: t(k.faqQ5), answer: t(k.faqA5) },
    { question: t(k.faqQ6), answer: t(k.faqA6) },
  ]

  return (
    <>
      {/* ─────────────── Hero + calculator ─────────────── */}
      <section className="pt-8 pb-16 md:pt-12 md:pb-24">
        <div className={CONTAINER}>
          {/* The widget owns its own layout — one centred column, with the
              result panel appearing under the form once Calculate has run —
              because the form and the result share state; the intro rides
              along in it so the h1 stays visible in this file. */}
          <Fib4CalculatorWidget
            intro={
              <>
                <h1 className="type-display-lg text-balance text-neutral-900">
                  {t(k.heading)}
                </h1>
                <p className="type-body-lg mt-5 text-neutral-600">
                  {t(k.subheading)}
                </p>
              </>
            }
          />
        </div>
      </section>

      {/* ─────────────── What FIB-4 is ─────────────── */}
      <section className={SECTION}>
        <div className={CONTAINER}>
          <div className={PROSE}>
            <p className="type-label-lg text-neutral-400">{t(k.whatLabel)}</p>
            <h2 className="type-display-sm mt-4 text-neutral-900">
              {t(k.whatHeading)}
            </h2>
            <div className="mt-6 space-y-5">
              <p className="type-body-lg text-neutral-600">{t(k.whatP1)}</p>
              <p className="type-body-lg text-neutral-600">{t(k.whatP2)}</p>
              <p className="type-body-lg text-neutral-600">{t(k.whatP3)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── The four inputs ─────────────── */}
      <section className="pb-16 md:pb-24">
        <div className={CONTAINER}>
          <div className={PROSE}>
            <p className="type-label-lg text-neutral-400">{t(k.inputsLabel)}</p>
            <h2 className="type-display-sm mt-4 text-neutral-900">
              {t(k.inputsHeading)}
            </h2>
            <p className="type-body-lg mt-4 text-neutral-600">
              {t(k.inputsIntro)}
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-[1128px] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {INPUTS.map(({ titleKey, bodyKey, icon }) => (
              <div key={titleKey} className="rounded-[16px] bg-white p-6">
                {icon}
                <h3 className="type-title-md mt-4 text-neutral-900">
                  {t(titleKey)}
                </h3>
                <p className="type-body-md mt-3 text-neutral-600">
                  {t(bodyKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── Cut-off reference table ─────────────── */}
      <section className="pb-16 md:pb-24">
        <div className={CONTAINER}>
          <div className={PROSE}>
            <h2 className="type-display-sm text-neutral-900">
              {t(k.tableHeading)}
            </h2>
            <p className="type-body-lg mt-4 text-neutral-600">
              {t(k.tableIntro)}
            </p>
          </div>

          {/* Wide table scrolls inside its own container on narrow viewports. */}
          <div className={cn(PROSE, "mt-8 overflow-x-auto")}>
            <table
              data-testid="fib4-threshold-table"
              className="w-full min-w-[640px] border-collapse text-left"
            >
              <caption className="sr-only">{t(k.tableHeading)}</caption>
              <thead>
                <tr className="border-b border-neutral-200">
                  <th
                    scope="col"
                    className="type-label-md pb-3 text-neutral-500 normal-case"
                  >
                    {t(k.tableColAge)}
                  </th>
                  {/* One column per band, from the same registry the result
                      card reads, so the table cannot describe a band the
                      widget does not assign. */}
                  {BAND_ORDER.map((band) => (
                    <th
                      key={band.id}
                      scope="col"
                      className="type-label-md pb-3 text-neutral-500 normal-case"
                    >
                      <span
                        className={cn(
                          "type-title-sm inline-block rounded-full px-3 py-1 whitespace-nowrap",
                          BAND_STYLES[band.id].chip
                        )}
                      >
                        {t(band.labelKey)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {AGE_GROUP_ORDER.map((group) => (
                  <tr
                    key={group.id}
                    className="border-b border-neutral-200 align-top last:border-0"
                  >
                    <th
                      scope="row"
                      className="type-title-sm py-4 pr-4 whitespace-nowrap text-neutral-900"
                    >
                      {t(group.labelKey)}
                    </th>
                    {group.thresholdKeys.map((thresholdKey) => (
                      <td
                        key={thresholdKey}
                        className="type-body-md py-4 pr-4 whitespace-nowrap text-neutral-900 tabular-nums"
                      >
                        {t(thresholdKey)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className={cn(PROSE, "type-body-sm mt-4 text-neutral-500")}>
            {t(k.tableNote)}
          </p>
        </div>
      </section>

      {/* ─────────────── Who it is for ─────────────── */}
      <section className={cn(SECTION, "bg-neutral-100")}>
        <div className={CONTAINER}>
          <div className={PROSE}>
            <h2 className="type-display-sm text-neutral-900">
              {t(k.whoHeading)}
            </h2>
            <p className="type-body-lg mt-4 text-neutral-600">
              {t(k.whoIntro)}
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-[760px] grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-[16px] bg-white p-6">
              <h3 className="type-title-lg text-neutral-900">
                {t(k.whoForTitle)}
              </h3>
              <ul className="mt-4 space-y-3">
                {WHO_FOR.map((key) => (
                  <li
                    key={key}
                    className="type-body-md flex items-start gap-3 text-neutral-600"
                  >
                    <GreenCheck
                      width={20}
                      height={20}
                      className="mt-0.5 shrink-0"
                    />
                    <span>{t(key)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-[16px] bg-white p-6">
              <h3 className="type-title-lg text-neutral-900">
                {t(k.whoNotForTitle)}
              </h3>
              <ul className="mt-4 space-y-3">
                {WHO_NOT_FOR.map((key) => (
                  <li
                    key={key}
                    className="type-body-md flex items-start gap-3 text-neutral-600"
                  >
                    <XCircleIcon
                      size={20}
                      className="mt-0.5 shrink-0 text-neutral-400"
                    />
                    <span>{t(key)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className={cn(PROSE, "type-body-sm mt-6 text-neutral-500")}>
            {t(k.whoNote)}
          </p>
        </div>
      </section>

      {/* ─────────────── The units ─────────────── */}
      {/* The one market-specific half of this page. FIB-4 itself has no country
          calibration, so what differs between markets is not the model but how
          a lab writes the four numbers down, and the platelet count is where
          getting that wrong silently divides a score by a thousand. */}
      <section className={SECTION}>
        <div className={CONTAINER}>
          <div className={PROSE}>
            <h2 className="type-display-sm text-neutral-900">
              {t(k.unitsHeading)}
            </h2>
            <div className="mt-6 space-y-5">
              <p className="type-body-lg text-neutral-600">{t(k.unitsP1)}</p>
              <p className="type-body-lg text-neutral-600">{t(k.unitsP2)}</p>
            </div>
            <div className="mt-8 flex items-start gap-4 rounded-[16px] bg-white p-6">
              <SwissShieldIcon size={24} className="mt-0.5 shrink-0" />
              <div>
                <h3 className="type-title-md text-neutral-900">
                  {t(k.unitsTipTitle)}
                </h3>
                <p className="type-body-md mt-2 text-neutral-600">
                  {t(k.unitsTipBody)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── Limitations ─────────────── */}
      <section className="pb-16 md:pb-24">
        <div className={CONTAINER}>
          <div className={PROSE}>
            <p className="type-label-lg text-neutral-400">{t(k.limitsLabel)}</p>
            <h2 className="type-display-sm mt-4 text-neutral-900">
              {t(k.limitsHeading)}
            </h2>
            <p className="type-body-lg mt-4 text-neutral-600">
              {t(k.limitsIntro)}
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-[1128px] grid-cols-1 gap-4 md:grid-cols-2">
            {LIMITS.map(({ titleKey, bodyKey, icon }) => (
              <div key={titleKey} className="rounded-[16px] bg-white p-6">
                {icon}
                <h3 className="type-title-lg mt-4 text-neutral-900">
                  {t(titleKey)}
                </h3>
                <p className="type-body-md mt-3 text-neutral-600">
                  {t(bodyKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── Book a membership ─────────────── */}
      <section
        className={SECTION}
        id="membership"
        data-testid="fib4-membership-section"
      >
        <div className={CONTAINER}>
          <div className="bg-aubergine-800 overflow-hidden rounded-[16px] px-6 py-12 md:px-12 md:py-16">
            <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-16">
              <div>
                <p className="type-label-lg text-aubergine-300">
                  {t(k.memLabel)}
                </p>
                <h2 className="type-display-sm mt-4 text-balance text-white">
                  {t(k.memHeading)}
                </h2>
                <p className="type-body-lg text-aubergine-100 mt-5">
                  {t(k.memBody)}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  {hasMembership ? (
                    <>
                      <BookingCta
                        forMembership
                        // `tonal` on purpose: this CTA sits on the aubergine
                        // panel, where `primary` would be aubergine-on-aubergine
                        // and stop reading as a button.
                        className={cn(
                          buttonVariants({ variant: "tonal" }),
                          "group"
                        )}
                        trackingLabel="fib4-calculator-membership-cta"
                        trackingLocation="tools/fib4-calculator"
                      >
                        {t(k.memCtaPrimary)}
                        <ArrowRightIcon className="ml-2 transition-transform duration-200 ease-out group-hover:translate-x-1" />
                      </BookingCta>
                      <Link
                        href={lp("/membership")}
                        className="type-title-md text-white underline underline-offset-4 transition-opacity hover:opacity-80"
                      >
                        {t(k.memCtaSecondary)}
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link
                        href={lp(`/tests/${baseBloodTest(country).slug}`)}
                        data-testid="fib4-checkup-cta"
                        className={cn(
                          buttonVariants({ variant: "tonal" }),
                          "group"
                        )}
                      >
                        {t(k.memCtaPrimary)}
                        <ArrowRightIcon className="ml-2 transition-transform duration-200 ease-out group-hover:translate-x-1" />
                      </Link>
                      <Link
                        href={lp("/pricing")}
                        className="type-title-md text-white underline underline-offset-4 transition-opacity hover:opacity-80"
                      >
                        {t(k.memCtaSecondary)}
                      </Link>
                    </>
                  )}
                </div>
                <p className="type-body-sm text-aubergine-300 mt-6">
                  {t(k.memFootnote)}
                </p>
              </div>

              <ul className="space-y-4">
                {MEMBERSHIP_FEATURES.map((key) => (
                  <li
                    key={key}
                    className="type-body-lg text-aubergine-100 flex items-start gap-3"
                  >
                    <GreenCheck
                      width={22}
                      height={22}
                      className="mt-0.5 shrink-0"
                    />
                    <span>{t(key)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── FAQ ─────────────── */}
      <FaqSection
        heading={t(k.faqHeading)}
        items={initialFaqs.length > 0 ? initialFaqs : faqFallback}
        linkLabel={null}
        allowMultiple
        currentPage="tools/fib4-calculator"
      />

      {/* ─────────────── Other calculators ─────────────── */}
      {/* Lists the other `/tools/*` calculators this market has, from the
          registry in `lib/health-tools.ts`, and renders nothing where there
          are none. Placed after the FAQ and before the closing CTA: a visitor
          who read the page is the one worth cross-linking, and it should not
          sit between the result and the conversion path. */}
      <OtherToolsSection currentToolId="fib4-calculator" />

      {/* ─────────────── Closing CTA ─────────────── */}
      <CtaBanner
        title={t(k.ctaHeading)}
        subtitle={t(k.ctaBody)}
        imageSrc={blobMediaUrl("/images/homepage/cta-bottom.jpeg")}
        primaryCta={{ label: t(k.ctaPrimary), href: lp("/pricing") }}
        secondaryCta={{ label: t(k.ctaSecondary), href: lp("/contact") }}
      />

      {/* ─────────────── Source + medical disclaimer ─────────────── */}
      {/* The source line is not decoration on this page: a clinical index is
          only quotable if the reader can see which index it is and which
          version of the cut-offs it uses. */}
      <section className="pt-10 pb-16">
        <div className={CONTAINER}>
          <div className={cn(PROSE, "flex items-start gap-4")}>
            <InfoIcon size={20} className="mt-1 shrink-0 text-neutral-400" />
            <div>
              <p className="type-label-md text-neutral-500 normal-case">
                {t(k.sourceLabel)}
              </p>
              <p className="type-body-sm mt-1 text-neutral-500">
                {t(k.sourceBody)}
              </p>
              <p className="type-body-sm mt-4 text-neutral-500">
                {t(k.disclaimer)}
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
